"use strict";

import Session from '../../Models/Session';
import PageProcessor from '../PageProcessor';
import ObjectHelper from '../../Helpers/ObjectHelper';
import Utils from '../../Helpers/Utils';
import ModalHelper from '../../Helpers/ModalHelper';
import Price from '../../Helpers/Price';
import DomHelper from '../../Helpers/Dom';

export default class SelectorPage extends PageProcessor {

	constructor() {
		super();

		this.activeRuntimes = null;
		this.selectorElement = null;
		this.finansSectionHidden = false;
		this.sparxpresDisplayControl = ".sparxpres_web_sale";
	}

	/**
	 * Initialize processor
	 * @return
	 */
	initialize() {
		let self = this;
		self.bindElements();
		self.addElementRefreshEvents();
		self.fetchPrice();

		if (!self.hasBeenInitialized) {
			self.hasBeenInitialized = true;
			self.onInitialization();
		}
	}

	onInitialization() {
		let self = this;

		self.addEventListener("sparxpresPeriodChange", function (event) {
			const newDuration = parseInt(event.detail.period);
			self.debug("SelectorPage.sparxpresPeriodChange event caught, new duration is %d", newDuration);

			self.duration = newDuration;
			Session.set('sparxpres-default-period', newDuration);

			self.getLoanDetails(self.getPriceCents(), newDuration)
				.then(loanDetails => self.updateViewData(loanDetails));
		});

		self.addEventListener("sparxpresPeriodInput", function (event) {
			self.debug("SelectorPage.sparxpresPeriodInput event caught");
			self.selectorElement.updatePeriodDisplay(parseInt(event.detail.period));
		});

		self.addEventListener('sparxpresInformationPageOpen', (event) => {
			self.debug("SelectorPage.sparxpresInformationPageOpen event caught");
			ModalHelper.showInformationPage(self.linkId, !!+self.settings.general.hasCheckout);
		});

		self.addEventListener('xpresPayInformationPageOpen', (event) => {
			self.debug("xpresPayInformationPageOpen event caught..");
			ModalHelper.showXpresPayPage(self.linkId);
		});

		self.addEventListener('sparxpresDirtyElements', (event) => {
			self.debug("SelectorPage.sparxpresDirtyElements event caught");
			let dirtyFields = event.detail.customData;

			if (dirtyFields.hasOwnProperty("price")) {
				const newPriceCents = Price.cleanPrice(dirtyFields["price"].newValue, self.isInDebugMode);
				self.debug("SelectorPage.sparxpresDirtyElements -> price: %s, cleaned: %s, getPrice: %s", dirtyFields["price"].newValue, newPriceCents, self.getPriceCents());

				if (self.getPriceCents() === newPriceCents) {
					self.debug("SelectorPage.sparxpresDirtyElements, newPrice: %d is identical to self.getPriceCents: %d, return....", newPriceCents, self.getPriceCents());
					return;
				}

				self.cleanedPriceCents = newPriceCents;
				if (self.elementBinding.getElement("sparxpresContent")) {
					if (newPriceCents < self.minimumLoanAmountCents) {
						if (!self.finansSectionHidden) {
							self.debug("SelectorPage.sparxpresDirtyElements, price (%d) is less than minimum (%d), hide finance element.", newPriceCents, self.minimumLoanAmountCents);
							DomHelper.hide("div.sparxpres_web_sale > div.sparxpres_" + self.pageType);
							self.finansSectionHidden = true;
						}
					} else {
						self.getLoanDetails(newPriceCents)
							.then(loanDetails => {
								if (!window._sparxpres.SparxpresElement) {
									this.debug("Sparxpres loan element not inserted, trying to insert...");
									let wElem = DomHelper.getElement("div." + self.getWrapperClass());
									if (wElem) {
										wElem.remove();
										self.finansSectionHidden = false;
										self.activeRuntimes = null;
										self.selectorElement = null;
										self.elementBinding.hardRefreshElement("sparxpresContent", true);
										self.priceLoaded(newPriceCents);
									}
								} else {
									self.updateViewData(loanDetails);
									if (self.finansSectionHidden) {
										DomHelper.show("div.sparxpres_web_sale > div.sparxpres_" + self.pageType);
										self.finansSectionHidden = false;
									}
								}
							})
							.catch(error => {console.log("Failed to fetch loan details");})
					}
				} else if (newPriceCents >= self.minimumLoanAmountCents) {
					// Load finance box
					self.priceLoaded(newPriceCents);
				}
			}
		});

		self.addEventListener('sparxpresNewPrice', (event) => {
			self.debug("SelectorPage.sparxpresNewPrice event caught");
			self.elementBinding.rebindElement('price', self.settings.scraping.fields.price);
		});

		self.appendCustomCssToHead(self.settings.display.customCSS);
	}

	/**
	 * Called when content is loaded
	 * @param content
	 * @param showFinance
	 * @param showXpresPay
	 */
	contentLoaded(content, showFinance = false, showXpresPay = false) {
		let self = this;

		self.debug("SelectorPage.contentLoaded start");

		if (self.elementBinding.getElement("sparxpresContent")) {
			self.updateContent();
		} else {
			const html = self.wrapHTMLInDisplayContainer(
				showFinance ? content.html.finance : '',
				showXpresPay ? content.html.xprespay : ''
			);

			self.attachContent(content.style, html)
				.then(() => {
					let initSelectorElement = false;

					// It works because sparxpresContent element hasn't been refreshed in between first attachment and this point.
					if (!self.elementBinding.getElement("sparxpresContent")) {
						self.elementBinding.hardRefreshElement("sparxpresContent");
						self.runScript(content.script);
						self.runScript(self.settings.display.customJS);
						initSelectorElement = true;
					} else {
						if (self.selectorElement === null) {
							initSelectorElement = true;
						}
					}

					if (initSelectorElement && showFinance) {
						let defaultPeriod = Session.get('sparxpres-default-period') || self.loanInformation.defaultPeriod;
						self.selectorElement = new window._sparxpres.SparxpresElement(self.loanInformation.loanPeriods, defaultPeriod);
						self.selectorElement.init(self.loanInformation.loanId);
					}

					self.afterAttachment();

					if (showFinance) {
						self.getLoanDetails()
							.then(loanDetails => {
								self.updateViewData(loanDetails);

								self.elementBinding.hardRefreshElement('displayControl');
								self.elementBinding.show('displayControl');
							})
							.catch(error => {
								self.elementBinding.hardRefreshElement('displayControl');
								self.elementBinding.hide('displayControl');
							});
					}
				});
		}
	}

	/**
	 * called when price is loaded
	 * @param priceCents
	 */
	priceLoaded(priceCents = 0) {
		let self = this;

		self.debug("SelectorPage.priceLoaded started with price %d", priceCents);
		if (priceCents < 1) {
			return;
		}

		if (!this.readyForAttach()) {
			self.debug("SelectorPage.priceLoaded, not ready for attach, returning...");
			return;
		}

		self.cleanedPriceCents = priceCents;

		self.getLoanInformation(priceCents, self.duration)
			.then(loanInfo => {
				self.minimumLoanAmountCents = Math.floor(loanInfo.minAmount * 100);
				const showFinance = Utils.isFinanceEnabled(loanInfo, priceCents);
				const showXpresPay = Utils.isXpresPayEnabled(loanInfo, priceCents);

				if (!showFinance && !showXpresPay) {
					self.elementBinding.removeFromDom("sparxpresContent");
					self.debug("SelectorPage.priceLoaded, finance and XpresPay is disabled, returning...");
					return;
				}

				if (self.elementBinding.getElement("sparxpresContent")) {
					if (showFinance) {
						self.updateContent();
					}
				} else {
					if (showFinance) {
						self.getContent()
							.then(content => {
								self.contentLoaded(content, showFinance, showXpresPay);
							});
					} else if (showXpresPay) {
						self.getXpresPayPageContent()
							.then(content => {
								self.contentLoaded(content, showFinance, showXpresPay);
							});
					}
				}
			})
			.catch(error => {
				console.debug("LI error: ", error);
			});

		// save or update cart price to session storage
		if (self.pageType === "cart") {
			const sessionKey = "sparxpres-checkout-collected-data";
			let checkoutData = Session.get(sessionKey);
			if (checkoutData === null) {
				checkoutData = {"cartPrice": priceCents};
			} else {
				checkoutData.cartPrice = priceCents;
			}
			Session.set(sessionKey, checkoutData);
		}
	}

	/**
	 * Bind elements tp the elementBinder
	 * @return
	 */
	bindElements() {
		const priceAttribute = this.settings.display.priceAttribute || '';
		this.elementBinding.addElement(
			"price",
			this.settings.scraping.fields.price,
			false,
			priceAttribute
		);

		this.elementBinding.addElements({
			"insertContainer": this.settings.display.insertContainer,
			"displayControl": this.sparxpresDisplayControl,
			"sparxpresContent": "." + this.getWrapperClass()
		});
	}

	/**
	 * Attaches events that should refresh data from the dom
	 * @return
	 */
	addElementRefreshEvents() {
		let self = this;
		if (self.hasBeenInitialized) return;

		const useCapture = self.settings.general.hasOwnProperty('useCaptureForRefreshEvents') && self.settings.general.useCaptureForRefreshEvents === "1";
		self.elementBinding.addRefreshEvent('change', true, () => {
			if (!self.active) return;

			self.debug("SelectorPage.addElementRefreshEvents, change event caught");
//			self.loadPrice().then(price => self.priceLoaded(price));
			self.loadPrice().then();	// self.priceLoaded is not called, because loadPrice triggers DirtyElements event

		}, useCapture);

		const priceObserverSelector = self.settings.display.priceObserverSelector;
		if (priceObserverSelector) {
			self.debug("SelectorPage.priceObserverSelector:", priceObserverSelector);
			const mutationContainer = document.querySelector(priceObserverSelector);
			self.debug("SelectorPage.mutationContainer:", mutationContainer);
			if (mutationContainer) {
				const mutationConfig = {
					attributes: true,
					childList: true,
					subtree: true,
					characterData: true,
					characterDataOldValue: false
				};

				const domObserver = new MutationObserver(function (mutations) {
					if (!self.active) return;

					self.debug('SelectorPage MutationObserver function called:', mutations);
					if (self.isInDebugMode) console.table(mutations);

//					self.loadPrice().then(price => self.priceLoaded(price));
					self.loadPrice().then();	// self.priceLoaded is not called, because loadPrice triggers DirtyElements event
				});
				domObserver.observe(mutationContainer, mutationConfig);
			}
		}
	}

	/**
	 * Update content with new loan details
	 * @return
	 */
	updateContent() {
		let self = this;

		if (!self.activeRuntimes
			|| !ObjectHelper.isEquivalentArrayOfObjects(self.activeRuntimes, self.loanInformation.loanPeriods)
		) {
			if (self.selectorElement === null) {
				if (!window._sparxpres.SparxpresElement) {
					return;
				}
				self.debug("SelectorPage.updateContent, SelectorElement is null, build it!!!!");
				let defaultPeriod = Session.get('sparxpres-default-period') || self.loanInformation.defaultPeriod;
				self.selectorElement = new window._sparxpres.SparxpresElement(self.loanInformation.loanPeriods, defaultPeriod);
				self.selectorElement.init(self.loanInformation.loanId);
			} else {
				self.debug("SelectorPage.updateContent, calling rePaintElement, loanId: %d", self.loanInformation.loanId);
				self.rePaintElement(self.loanInformation);
			}

			self.activeRuntimes = self.loanInformation.loanPeriods;
		}

		self.getLoanDetails().then(loanDetails => self.updateViewData(loanDetails));
	}

	/**
	 * Add functionality to run after content has been added to the dom
	 * @return
	 */
	afterAttachment(script= '') {
		super.afterAttachment(script);

		this.activeRuntimes = this.loanInformation.loanPeriods;
		this.duration = this.selectorElement ? this.selectorElement.getCurrentValue() : this.duration;
	}

	/**
	 * Fetch price
	 */
	fetchPrice(retryCnt = 0, maxRetryCnt = 5) {
		let self = this;
		self.loadPrice()
			.then(price => {
				if (price > 0) {
					self.priceLoaded(price);
				} else {
					if (retryCnt < maxRetryCnt) {
						self.debug("SelectorPage.fetchPrice, price not found, wait %d seconds before retrying, retry %d of %d", (retryCnt + 1) * 2, retryCnt + 1, maxRetryCnt);
						Utils.delay(1000 * ((retryCnt + 1) * 2)).then(() => {
							self.fetchPrice(++retryCnt, maxRetryCnt);
						});
					} else {
						Utils.info("We didn't find the price.");
					}
				}
			});
	}

	/**
	 * Load price
	 * @return {Promise}
	 */
	loadPrice() {
		let self = this;

		try {
			self.elementBinding.hardRefreshElement('price');
			const priceElemValue = self.elementBinding.getValue('price') || '';
			let priceCents = Price.cleanPrice(priceElemValue, self.isInDebugMode);
			return Promise.resolve(priceCents > 0 ? priceCents : 0);
		} catch (error) {
			return Promise.resolve(0);
		}
	}

	/**
	 * rePaint element
	 * @param {Object} loanInfo
	 */
	rePaintElement(loanInfo = {}) {
		this.debug("SelectorPage.rePaintElement with loanId: %d", loanInfo.loanId);
		this.selectorElement
			.setIntervals(loanInfo.loanPeriods)
			.rePaint(loanInfo.loanId);
	}
}